import { useState, useEffect, useCallback } from 'react';
import { contattiService, magazziniService, viaggiService, authService } from '../services/supabase';
import { 
  mapContattoFromSupabase,
  mapMagazzinoFromSupabase,
  mapViaggioFromSupabase
} from '../utils/formatters';

// Hook per gestire l'autenticazione
export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkUser = async () => {
      try {
        const currentUser = await authService.getCurrentUser();
        setUser(currentUser);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    checkUser();
  }, []);

  const signIn = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const { user } = await authService.signIn(email, password);
      setUser(user);
      return user;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const signUp = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const { user } = await authService.signUp(email, password);
      return user;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await authService.signOut();
      setUser(null);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  return { user, loading, error, signIn, signUp, signOut };
};

// Hook per gestire i contatti
export const useContatti = () => {
  const [contatti, setContatti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchContatti = useCallback(async () => {
    setLoading(true);
    try {
      const data = await contattiService.getAll();
      setContatti(data.map(mapContattoFromSupabase));
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContatti();
  }, [fetchContatti]);

  const addContatto = useCallback(async (contatto) => {
    try {
      const newContatto = await contattiService.add(contatto);
      setContatti(prev => [...prev, mapContattoFromSupabase(newContatto)]);
      return newContatto;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const updateContatto = useCallback(async (contatto) => {
    try {
      const updatedContatto = await contattiService.update(contatto);
      setContatti(prev => 
        prev.map(c => c.id === contatto.id ? mapContattoFromSupabase(updatedContatto) : c)
      );
      return updatedContatto;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const deleteContatto = useCallback(async (id) => {
    try {
      await contattiService.delete(id);
      setContatti(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  return { 
    contatti, 
    clienti: contatti.filter(c => c.isCliente),
    vettori: contatti.filter(c => c.isVettore),
    loading, 
    error, 
    fetchContatti, 
    addContatto, 
    updateContatto, 
    deleteContatto 
  };
};

// Hook per gestire i magazzini
export const useMagazzini = () => {
  const [magazzini, setMagazzini] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMagazzini = useCallback(async () => {
    setLoading(true);
    try {
      const data = await magazziniService.getAll();
      setMagazzini(data.map(mapMagazzinoFromSupabase));
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMagazzini();
  }, [fetchMagazzini]);

  const addMagazzino = useCallback(async (magazzino) => {
    try {
      const newMagazzino = await magazziniService.add(magazzino);
      setMagazzini(prev => [...prev, mapMagazzinoFromSupabase(newMagazzino)]);
      return newMagazzino;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const updateMagazzino = useCallback(async (magazzino) => {
    try {
      const updatedMagazzino = await magazziniService.update(magazzino);
      setMagazzini(prev => 
        prev.map(m => m.id === magazzino.id ? mapMagazzinoFromSupabase(updatedMagazzino) : m)
      );
      return updatedMagazzino;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const deleteMagazzino = useCallback(async (id) => {
    try {
      await magazziniService.delete(id);
      setMagazzini(prev => prev.filter(m => m.id !== id));
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  return { 
    magazzini, 
    loading, 
    error, 
    fetchMagazzini, 
    addMagazzino, 
    updateMagazzino, 
    deleteMagazzino 
  };
};

// Hook per gestire i viaggi
export const useViaggi = () => {
  const [viaggi, setViaggi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchViaggi = useCallback(async () => {
    setLoading(true);
    try {
      const data = await viaggiService.getAll();
      setViaggi(data.map(mapViaggioFromSupabase));
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchViaggiBetweenDates = useCallback(async (startDate, endDate) => {
    setLoading(true);
    try {
      const data = await viaggiService.getByDateRange(startDate, endDate);
      return data.map(mapViaggioFromSupabase);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchViaggi();
  }, [fetchViaggi]);

  const addViaggio = useCallback(async (viaggio) => {
    try {
      // Assicuriamoci che il campo note sia presente anche se vuoto
      const viaggioToAdd = {
        ...viaggio,
        note: viaggio.note || '' // Assicuriamoci che note sia sempre definito
      };
      
      const newViaggio = await viaggiService.add(viaggioToAdd);
      setViaggi(prev => [mapViaggioFromSupabase(newViaggio), ...prev]);
      return newViaggio;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const updateViaggio = useCallback(async (viaggio) => {
    try {
      // Assicuriamoci che il campo note sia presente anche se vuoto
      const viaggioToUpdate = {
        ...viaggio,
        note: viaggio.note || '' // Assicuriamoci che note sia sempre definito
      };
      
      const updatedViaggio = await viaggiService.update(viaggioToUpdate);
      setViaggi(prev => 
        prev.map(v => v.id === viaggio.id ? mapViaggioFromSupabase(updatedViaggio) : v)
      );
      return updatedViaggio;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const deleteViaggio = useCallback(async (id) => {
    try {
      await viaggiService.delete(id);
      setViaggi(prev => prev.filter(v => v.id !== id));
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  return { 
    viaggi, 
    loading, 
    error, 
    fetchViaggi,
    fetchViaggiBetweenDates,
    addViaggio, 
    updateViaggio, 
    deleteViaggio 
  };
};